import { Check, CircleCheck, Lock, RotateCcw, Save } from 'lucide-react';
import type { FormEvent, RefObject } from 'react';
import { Button } from '#client/components/ui/Button';

interface EntryActionBarProps {
  entered: number;
  total: number;
  allEntered: boolean;
  isCompleted: boolean;
  canEdit: boolean;
  canReopen: boolean;
  submitting: boolean;
  saveRef: RefObject<HTMLButtonElement | null>;
  onComplete: (event: FormEvent) => void;
  onReopen: (event: FormEvent) => void;
}

/**
 * The sticky bar at the foot of the entry screen.
 *
 * Sticky rather than in the page header: on a long test the header scrolls
 * away, and Save should never be somewhere you have to scroll back to find.
 *
 * There is no status dropdown. A free-choice status would let reports sit in
 * `registered` forever and let a half-empty one be marked completed; the
 * transition is derived on save, or requested explicitly and refused if
 * incomplete.
 */
export function EntryActionBar({
  entered,
  total,
  allEntered,
  isCompleted,
  canEdit,
  canReopen,
  submitting,
  saveRef,
  onComplete,
  onReopen,
}: EntryActionBarProps) {
  return (
    <div className="c-entry__actions">
      <span className="c-entry__progress">
        {allEntered ? (
          <span className="c-entry__ready">
            <CircleCheck aria-hidden="true" />
            {total === 1 ? 'Ready to complete' : `All ${total} results entered`}
          </span>
        ) : total === 1 ? (
          'Not ready to complete'
        ) : (
          `${total - entered} of ${total} still to enter`
        )}
      </span>

      {isCompleted ? (
        canReopen && (
          <Button icon={<RotateCcw />} loading={submitting} onClick={onReopen}>
            Reopen report
          </Button>
        )
      ) : (
        <>
          <Button
            ref={saveRef}
            type="submit"
            icon={<Save />}
            loading={submitting}
            disabled={!canEdit}
          >
            Save draft
          </Button>

          <Button
            variant="primary"
            icon={allEntered ? <Check /> : <Lock />}
            loading={submitting}
            // Guarded here and refused by the server, which returns a
            // field-level error naming how many results are still blank.
            disabled={!canEdit || !allEntered}
            title={
              allEntered
                ? 'Complete this report (Ctrl+Enter)'
                : `Enter all ${total} results to complete this report`
            }
            onClick={onComplete}
          >
            Complete report
          </Button>
        </>
      )}
    </div>
  );
}
